import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import React, { useCallback, useEffect, useState } from 'react';

interface SignedHtmlContractPreviewProps {
  downloadUrl?: string | null;
  inlineUrl?: string | null;
  htmlContent?: string | null;
  title?: string;
}

const fetchHtmlTextFromUrl = async (url: string): Promise<string> => {
  const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
  if (!res.ok) throw new Error(`HTML 조회 실패 (${res.status})`);
  const text = await res.text();
  if (!text.trim()) throw new Error('HTML 내용이 비어 있습니다.');
  return text;
};

export const SignedHtmlContractPreview: React.FC<
  SignedHtmlContractPreviewProps
> = ({
  downloadUrl,
  inlineUrl,
  htmlContent: htmlContentProp,
  title = '서명된 위탁 계약 HTML',
}) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hasSource = Boolean(
    htmlContentProp?.trim() || inlineUrl || downloadUrl,
  );

  useEffect(() => {
    if (!hasSource) {
      setHtml('');
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setHtml('');

      try {
        if (htmlContentProp?.trim()) {
          if (!cancelled) setHtml(htmlContentProp);
          return;
        }

        const urlCandidates = [inlineUrl, downloadUrl].filter(Boolean) as string[];
        for (const url of urlCandidates) {
          try {
            const text = await fetchHtmlTextFromUrl(url);
            if (!cancelled) setHtml(text);
            return;
          } catch (fetchErr) {
            if (url === urlCandidates[urlCandidates.length - 1]) {
              throw fetchErr;
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [hasSource, htmlContentProp, inlineUrl, downloadUrl]);

  const openInNewTab = useCallback(() => {
    if (!html.trim()) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [html]);

  if (!hasSource) {
    return (
      <p className="text-sm text-muted-foreground">
        서명 HTML 미리보기를 불러올 수 없습니다.
      </p>
    );
  }

  if (loading) {
    return <LoadingSpinner tip="서명 HTML을 불러오는 중..." />;
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        HTML 미리보기 로드 실패: {error.message}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{title}</p>
        <Button type="button" variant="outline" size="sm" onClick={openInNewTab}>
          새 창에서 보기
        </Button>
      </div>
      <iframe
        title={title}
        srcDoc={html}
        className="h-[800px] w-full rounded-lg border bg-white"
        sandbox="allow-same-origin"
      />
    </div>
  );
};
